import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteReview, fetchReviews, updateReview } from '../../api/reviewsModule';
import RoleGuard from '../../components/RoleGuard';
import { useToast } from '../../components/ToastContext';
import useModuleAuth from '../../hooks/useModuleAuth';

const sources = ['WEB', 'BOOKING', 'AIRBNB', 'OTHER'];

const RatingRow = ({ rating, count, total }) => {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-10 text-slate-600">{rating} star</span>
      <div className="flex-1 rounded-full bg-slate-100">
        <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right text-slate-500">{count}</span>
    </div>
  );
};

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
    <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-lg">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600"
        >
          Close
        </button>
      </div>
      <div className="pt-4">{children}</div>
    </div>
  </div>
);

const ReviewsPage = () => {
  const { isAdmin } = useModuleAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    rating: '',
    source: '',
    from: '',
    to: '',
    search: '',
  });
  const [page, setPage] = useState(1);
  const [editReview, setEditReview] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['reviews', filters, page],
    queryFn: () => fetchReviews({ ...filters, page, limit: 10 }),
    enabled: isAdmin,
  });

  const items = data?.data || [];
  const meta = data?.meta || {};

  const totalReviews = useMemo(() => Object.values(meta.ratingCounts || {}).reduce((acc, val) => acc + val, 0), [
    meta.ratingCounts,
  ]);

  useEffect(() => {
    setPage(1);
  }, [filters.rating, filters.source, filters.from, filters.to, filters.search]);

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateReview(id, payload),
    onSuccess: () => {
      toast.success('Review updated');
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      setEditReview(null);
    },
    onError: (err) => toast.error(err?.message || 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteReview,
    onSuccess: () => {
      toast.success('Review deleted');
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: (err) => toast.error(err?.message || 'Delete failed'),
  });

  if (!isAdmin) {
    return (
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <p className="text-sm text-slate-600">Only admins can view global reviews.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-700">Customers</p>
            <h1 className="text-xl font-bold text-slate-900">Reviews & Ratings</h1>
            <p className="text-sm text-slate-600">Monitor ratings and feedback across customers.</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Average rating</p>
            <p className="text-2xl font-semibold text-slate-900">{(meta.avgRating || 0).toFixed(1)}</p>
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-[1fr_1.2fr]">
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => (
              <RatingRow
                key={rating}
                rating={rating}
                count={meta.ratingCounts?.[rating] || 0}
                total={totalReviews}
              />
            ))}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <input
              type="search"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              placeholder="Search customer"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <select
              value={filters.rating}
              onChange={(event) => setFilters((prev) => ({ ...prev, rating: event.target.value }))}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              <option value="">All ratings</option>
              {[5, 4, 3, 2, 1].map((rating) => (
                <option key={rating} value={rating}>
                  {rating} stars
                </option>
              ))}
            </select>
            <select
              value={filters.source}
              onChange={(event) => setFilters((prev) => ({ ...prev, source: event.target.value }))}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              <option value="">All sources</option>
              {sources.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={filters.from}
                onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
              <input
                type="date"
                value={filters.to}
                onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        {error && (
          <div className="mb-4 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error.message || 'Failed to load reviews'}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-slate-500">
                    Loading...
                  </td>
                </tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-slate-500">
                    No reviews found.
                  </td>
                </tr>
              )}
              {items.map((review) => (
                <tr key={review.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    {review.customer?.firstName} {review.customer?.lastName}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{review.rating} / 5</td>
                  <td className="px-4 py-3 text-slate-700">{review.source}</td>
                  <td className="px-4 py-3 text-slate-700">{review.title}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <RoleGuard>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setEditReview(review)}
                          className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('Delete this review?')) {
                              deleteMutation.mutate(review.id);
                            }
                          }}
                          className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      </div>
                    </RoleGuard>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
          <div>
            Page {meta.page || 1} of {Math.max(1, meta.totalPages || 1)}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={(meta.page || 1) <= 1}
              className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={(meta.page || 1) >= (meta.totalPages || 1)}
              className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {editReview && (
        <Modal title="Edit review" onClose={() => setEditReview(null)}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              updateMutation.mutate({
                id: editReview.id,
                payload: {
                  title: formData.get('title'),
                  comment: formData.get('comment'),
                  rating: Number(formData.get('rating')),
                  source: formData.get('source'),
                },
              });
            }}
            className="space-y-3"
          >
            <input
              name="title"
              defaultValue={editReview.title}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <textarea
              name="comment"
              defaultValue={editReview.comment}
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <div className="grid gap-3 md:grid-cols-2">
              <select
                name="rating"
                defaultValue={editReview.rating}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              >
                {[5, 4, 3, 2, 1].map((rating) => (
                  <option key={rating} value={rating}>
                    {rating} stars
                  </option>
                ))}
              </select>
              <select
                name="source"
                defaultValue={editReview.source}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              >
                {sources.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default ReviewsPage;
